/**
 * Παρακολούθηση μιας εκτέλεσης που τρέχει ΣΤΟ ΠΑΡΑΣΚΗΝΙΟ (§11.9, §13.19.ΣΤ).
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ: από τη Φ5 το `POST …/push` και το `POST …/migrate` απαντούν
 * **202** μόλις γραφτεί η γραμμή της εκτέλεσης και συνεχίζουν μόνα τους. Μια
 * αποστολή 20 καταστημάτων γράφει σειριακά, με ελάχιστο κενό 1΄΄ ανά κλήση και
 * παράθυρο επαλήθευσης 60΄΄ ανά κατάστημα — δηλαδή ~20΄. Κανένα αίτημα HTTP δεν
 * ζει τόσο: το `requestTimeout` του Node είναι 300΄΄ και κάθε reverse proxy κόβει
 * νωρίτερα. Πριν από αυτό, η οθόνη έδειχνε «Αποτυχία αποστολής» για εκτέλεση που
 * ΣΥΝΕΧΙΖΕ να γράφει στο efood — το χειρότερο δυνατό ψέμα σε μη αναστρέψιμη
 * ενέργεια.
 *
 * ΚΑΝΟΝΑΣ: αποτυχία του *ερωτήματος* ΔΕΝ είναι αποτυχία της *εκτέλεσης*. Γι'
 * αυτό το `unreachable` είναι ξεχωριστό πεδίο και το κείμενό του λέει «δες το
 * Ιστορικό», ποτέ «απέτυχε».
 */

import { useEffect, useRef, useState } from 'react';
import {
  isTerminalRunStatus, orgPlatformCatalogAPI, RunDetail, RunProgress, RUN_POLL_MS,
} from '../../services/platformCatalogApi';

export const RUN_UNREACHABLE_TEXT =
  'Η οθόνη δεν μπορεί να διαβάσει την πρόοδο αυτή τη στιγμή — η εκτέλεση ΣΥΝΕΧΙΖΕΙ στον server. '
  + 'Δες το «Ιστορικό»· μην ξαναστείλεις.';

/**
 * Ρωτά `GET …/runs/:id` κάθε {@link RUN_POLL_MS} όσο η εκτέλεση δεν έχει τελειώσει.
 *
 * Η επόμενη ερώτηση μπαίνει σε ουρά ΜΟΝΟ όταν απαντηθεί η προηγούμενη
 * (`setTimeout` αλυσιδωτά, όχι `setInterval`): με αργό server ένα interval θα
 * στοίβαζε αιτήματα και θα έδειχνε παλιότερη κατάσταση μετά από νεότερη.
 *
 * @param runId `null` = δεν παρακολουθείται τίποτα (καθαρίζει την κατάσταση).
 * @param onFinished καλείται ΜΙΑ φορά, στην πρώτη τελική κατάσταση.
 */
export const useRunWatch = (
  orgId: number,
  runId: number | null,
  onFinished?: (detail: RunDetail | null) => void
): RunProgress | null => {
  const [progress, setProgress] = useState<RunProgress | null>(null);
  // Η callback αλλάζει ταυτότητα σε κάθε render του γονέα· αν έμπαινε στα
  // dependencies, κάθε render θα ξανάρχιζε την παρακολούθηση από την αρχή.
  const finishedRef = useRef<((detail: RunDetail | null) => void) | undefined>(undefined);
  finishedRef.current = onFinished;

  useEffect(() => {
    if (runId === null) {
      setProgress(null);
      return;
    }
    let alive = true;
    let timer: number | null = null;
    setProgress({ run_id: runId, detail: null, unreachable: null, finished: false });

    const ask = async () => {
      try {
        const detail = await orgPlatformCatalogAPI.getRun(orgId, runId);
        if (!alive) return;
        const finished = isTerminalRunStatus(detail ? detail.status : null);
        setProgress({ run_id: runId, detail: detail || null, unreachable: null, finished });
        if (finished) {
          if (finishedRef.current) finishedRef.current(detail || null);
          return;
        }
      } catch (err) {
        if (!alive) return;
        // ΚΡΑΤΑΜΕ την τελευταία γνωστή εικόνα: το ότι δεν απάντησε το ερώτημα
        // δεν σβήνει ό,τι ξέραμε πριν.
        setProgress(prev => ({
          run_id: runId,
          detail: prev ? prev.detail : null,
          unreachable: RUN_UNREACHABLE_TEXT,
          finished: false,
        }));
      }
      if (alive) timer = window.setTimeout(ask, RUN_POLL_MS);
    };

    ask();
    return () => {
      alive = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [orgId, runId]);

  return progress;
};

export default useRunWatch;
